import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import { useRouter } from 'next/router';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { REQUEST_BOT_RESET } from '../../lib/graphql/mutations';
import { Row } from '../Flex';
import { CardHeader, CardLicenseDetails } from './card-components/purchase';
import CardFooter from './card-components/purchase/CardFooter';
import CardKey from './card-components/purchase/CardKey';
import { analytics } from '../../lib/analytics';
import { showIntercomWindow } from 'next-intercom';
import { toast } from 'react-toastify';
import { CardContainer } from './card-components/common/CardContainer';
import ExpediteModal from './ExpediteModal'; // We'll create this component next
import { ExtendPurchaseModal } from './extend-purchase';

type StatusProps = {
  border: string;
  background: string;
  accent: string;
};

type Props = {
  id: string;
  botId: string;
  botName: string;
  botImage: string;
  orderReference: string;
  status: StatusProps;
  rentalDays: number;
  startDate: Dayjs;
  endDate: Dayjs;
  createdAt: Dayjs;
  lastResetRequestAt: string;
  expiresAt: string;
  email: string;
  password: string;
  secondaryKey: string;
  listingId: string;
  listingKey: string;
  macDownloadUrl: string;
  windowsDownloadUrl: string;
  discordSupportChannelGuildId: string;
  discordSupportChannelId: string;
  guideUrl: string;
  type: number;
  buyerEmail?: string;
  os: [];
  tags: [];
  listingMode: string;
  authType: string;
};

const CustomRow = styled(Row)`
  justify-content: space-between;
`;

const SeparatorLine = styled.div`
  border-bottom: 1px solid #e5e8f0;
  padding-bottom: 20px;
  height: 20px;
  margin-bottom: 20px;
`;

const PurchaseColorButton = styled.button<{
  backgroundColor: string;
}>`
  background-color: ${(props) => props.backgroundColor};
  border-radius: 10px;
  height: 42px;
  width: 100%;
  font-weight: 600;
  font-size: 14px;
  line-height: 17px;
  text-align: center;
  color: #ffffff;
`;

const PurchaseButtonRow = styled.div`
  display: flex;
  grid-gap: 11px;
  align-items: center;
  margin-bottom: 11px;
`;

const Purchase = (props: Props) => {
  const router = useRouter();
  const [requestBotReset, requestBotResetResult] =
    useMutation(REQUEST_BOT_RESET);
  const [showExpediteModal, setShowExpediteModal] = useState(false);
  const [showExtendModal, setShowExtendModal] = useState(false);

  const canReset =
    !props.lastResetRequestAt ||
    dayjs(new Date(props.lastResetRequestAt))
      .add(30, 'minutes')
      .isBefore(dayjs(new Date()));

  const onOpenTicket = () => {
    window.open(
      `https://discord.com/channels/${props.discordSupportChannelGuildId}/${props.discordSupportChannelId}`,
      '_blank',
      'noopener',
    );
  };

  const onOpenSupport = useCallback(() => {
    return showIntercomWindow();
  }, []);

  let resetButtonText = 'Reset';

  if (requestBotResetResult.loading) {
    resetButtonText = 'Loading...';
  } else if (requestBotResetResult.called) {
    resetButtonText = requestBotResetResult.error
      ? 'Error (Try again)'
      : 'Notification Sent';
  }

  const onOpenGuide = () => {
    if (props.listingMode === 'DIRECT') {
      window.open(props.guideUrl, '_blank', 'noopener');
    } else {
      router.push(
        `/bots/guides/bot/${props.botId}?channel=${props.discordSupportChannelId}`,
      );
    }
  };

  const handleKeyReset = () => {
    if (props.listingMode === 'DIRECT' && props.botId === 'cybersole') {
      return toast.error('You must reset on the cyber dashboard');
    }

    if (canReset && !requestBotResetResult.called)
      requestBotReset({ variables: { orderId: props.id } })
        .then(() => {
          analytics.track('User Requested Bot Reset', {
            orderId: props.id,
          });
        })
        .catch(() => {});
  };

  const handleViewNewListings = () => {
    analytics.track('User Clicked Purchase Again');
    router.push(`/bots/${props.botId}`);
  };

  const handleExtendOrder = () => {
    setShowExtendModal(true);
  };

  const handleExpedite = () => {
    setShowExpediteModal(true);
  };

  const isFuturePurchase = dayjs(props.startDate).isAfter(dayjs());

  return (
    <CardContainer>
      <CustomRow>
        <CardHeader
          id={props.id}
          botImage={props.botImage}
          botName={props.botName}
          listingMode={props.listingMode}
          tags={props.tags}
          dataTour={'step-3'}
          type={props.type}
          startDate={props.startDate}
          endDate={props.endDate}
          rentalDays={props.rentalDays}
          createdAt={props.createdAt}
        />
      </CustomRow>
      <SeparatorLine />
      <CardLicenseDetails
        type={props.type}
        startDate={props.startDate}
        endDate={props.endDate}
        dataTour={'step-4'}
      />
      <SeparatorLine />
      <CardKey
        secondaryKey={props.secondaryKey}
        listingKey={props.listingKey}
        email={props.email}
        password={props.password}
        type={props.type}
        botId={props.botId}
        buyerEmail={props.buyerEmail}
        dataTour={'step-5'}
        authType={props.authType}
      />
      <SeparatorLine />
      {props.type == 0 && (
        <CardFooter
          botId={props.botId}
          listingKey={props.listingKey}
          windowsDownloadUrl={props.windowsDownloadUrl}
          macDownloadUrl={props.macDownloadUrl}
          dataTour={'step-6'}
        />
      )}

      {props.type == 0 && isFuturePurchase && (
        <PurchaseButtonRow data-tour="step-11">
          <PurchaseColorButton
            onClick={handleExpedite}
            backgroundColor="#FF9800"
          >
            Expedite
          </PurchaseColorButton>
        </PurchaseButtonRow>
      )}

      {props.type == 0 && (
        <>
          <PurchaseButtonRow data-tour="step-7">
            <PurchaseColorButton
              onClick={onOpenGuide}
              backgroundColor="#1CBAF0"
            >
              View Guide
            </PurchaseColorButton>
            {props.listingMode === 'DIRECT' ? (
              <PurchaseColorButton
                onClick={onOpenSupport}
                backgroundColor="#6046FF"
              >
                Open Support
              </PurchaseColorButton>
            ) : (
              <PurchaseColorButton
                onClick={onOpenTicket}
                backgroundColor="#6046FF"
              >
                Open Ticket
              </PurchaseColorButton>
            )}
          </PurchaseButtonRow>
          <PurchaseButtonRow data-tour="step-8">
            <PurchaseColorButton
              onClick={handleKeyReset}
              backgroundColor="#F35C8A"
            >
              {resetButtonText}
            </PurchaseColorButton>
            <PurchaseColorButton
              onClick={handleExtendOrder}
              backgroundColor="#4CAF50"
            >
              Extend
            </PurchaseColorButton>
          </PurchaseButtonRow>
        </>
      )}

      {props.type == 1 && (
        <>
          <PurchaseButtonRow data-tour="step-9">
            <PurchaseColorButton
              onClick={onOpenGuide}
              backgroundColor="#1CBAF0"
            >
              View Guide
            </PurchaseColorButton>
            <PurchaseColorButton
              onClick={handleExpedite}
              backgroundColor="#FF9800"
            >
              Expedite
            </PurchaseColorButton>
          </PurchaseButtonRow>
          <PurchaseButtonRow>
            {props.listingMode === 'DIRECT' ? (
              <PurchaseColorButton
                onClick={onOpenSupport}
                backgroundColor="#6046FF"
              >
                Open Support
              </PurchaseColorButton>
            ) : (
              <PurchaseColorButton
                onClick={onOpenTicket}
                backgroundColor="#6046FF"
                disabled={true}
              >
                Open Ticket
              </PurchaseColorButton>
            )}
          </PurchaseButtonRow>
        </>
      )}

      {props.type == 2 && (
        <PurchaseButtonRow data-tour="step-10">
          <PurchaseColorButton
            onClick={handleViewNewListings}
            backgroundColor="#1CBAF0"
          >
            Purchase Again
          </PurchaseColorButton>
        </PurchaseButtonRow>
      )}

      {showExpediteModal && (
        <ExpediteModal
          orderId={props.id}
          botId={props.botId}
          botName={props.botName}
          currentStartDate={props.startDate}
          listingId={props.listingId} // Add this line
          onClose={() => setShowExpediteModal(false)}
        />
      )}

      {showExtendModal && (
        <ExtendPurchaseModal
          id={props.id}
          open={showExtendModal}
          onClose={() => setShowExtendModal(false)}
        />
      )}
    </CardContainer>
  );
};

export default Purchase;
